import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../App';

const RANK = { admin: 3, editor: 2, viewer: 1 };
const INIT = { first_name:'', last_name:'', email:'', phone:'', company:'', industry:'', city:'', country:'Cyprus', tags:'', source:'', status:'active', notes:'' };
const PAGE_SIZES = [50, 100, 250, 500, 1000];

const CopyBtn = ({ value, label }) => {
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    toast.success(`${label || 'Αντιγράφηκε'}: ${value.length > 40 ? value.slice(0,40)+'…' : value}`, { duration: 1800 });
  };
  if (!value) return null;
  return (
    <button onClick={copy} title="Αντιγραφή" style={{ background:'none', border:'none', cursor:'pointer', padding:'3px', color:'var(--mist)', display:'inline-flex', borderRadius:'4px' }}
      onMouseOver={e=>e.currentTarget.style.color='var(--blue)'} onMouseOut={e=>e.currentTarget.style.color='var(--mist)'}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.8"/></svg>
    </button>
  );
};

const TagChips = ({ tags, max = 3 }) => {
  if (!tags) return <span style={{ color:'var(--cloud)' }}>—</span>;
  const list = tags.split(',').map(t=>t.trim()).filter(Boolean);
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', alignItems:'center' }}>
      {shown.map((t,i) => <span key={i} className="badge badge-gray" style={{ fontSize:'10px' }}>{t}</span>)}
      {extra > 0 && <span style={{ fontSize:'10px', color:'var(--mist)', fontWeight:600 }}>+{extra}</span>}
    </div>
  );
};

export default function Contacts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = RANK[user.role] >= RANK['editor'];
  const isAdmin = user.role === 'admin';

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status:'', city:'', industry:'', source:'', tag:'' });
  const [opts, setOpts] = useState({ cities:[], industries:[], sources:[], tags:[] });
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('DESC');
  const [modal, setModal] = useState(null);       // 'form' | 'bulk-status' | 'bulk-tag'
  const [viewContact, setViewContact] = useState(null);  // contact detail card
  const [form, setForm] = useState(INIT);
  const [editId, setEditId] = useState(null);
  const [bulkVal, setBulkVal] = useState('');

  const fetchOpts = () => axios.get('/api/contacts/meta/filters').then(r => setOpts(r.data)).catch(()=>{});

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, search, sort_by: sortBy, sort_dir: sortDir, ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await axios.get('/api/contacts', { params });
      setContacts(data.contacts); setTotal(data.total); setPages(data.pages); setSelected([]);
    } catch { toast.error('Σφάλμα φόρτωσης'); } finally { setLoading(false); }
  }, [page, limit, search, filters, sortBy, sortDir]);

  useEffect(() => { fetchOpts(); }, []);
  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleSort = col => { if (sortBy===col) setSortDir(d=>d==='ASC'?'DESC':'ASC'); else { setSortBy(col); setSortDir('ASC'); } setPage(1); };
  const openAdd = () => { setForm(INIT); setEditId(null); setModal('form'); };
  const openEdit = c => { setForm({ ...c, tags: c.tags||'' }); setEditId(c.id); setModal('form'); setViewContact(null); };

  const handleSave = async () => {
    try {
      if (editId) await axios.put(`/api/contacts/${editId}`, form);
      else await axios.post('/api/contacts', form);
      toast.success(editId ? 'Ενημερώθηκε' : 'Προστέθηκε');
      setModal(null); fetchContacts(); fetchOpts();
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Διαγραφή;')) return;
    try { await axios.delete(`/api/contacts/${id}`); toast.success('Διαγράφηκε'); setViewContact(null); fetchContacts(); }
    catch { toast.error('Σφάλμα'); }
  };

  const handleBulk = async (action, value) => {
    try {
      const { data } = await axios.post('/api/contacts/bulk', { action, ids: selected, value });
      toast.success(data.message); fetchContacts(); setModal(null); setBulkVal('');
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const handleExport = format => {
    const ids = selected.length ? `?ids=${selected.join(',')}` : '';
    window.open(`/api/contacts/export/${format}${ids}`, '_blank');
  };

  const handleImport = async e => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    toast.loading('Import σε εξέλιξη...', { id: 'imp' });
    try {
      const { data } = await axios.post('/api/contacts/import', fd);
      toast.success(`Εισήχθησαν ${data.imported} | Παρακάμφθηκαν ${data.skipped}`, { id: 'imp', duration: 5000 });
      fetchContacts(); fetchOpts(); e.target.value = '';
    } catch { toast.error('Σφάλμα import', { id: 'imp' }); }
  };

  const sendEmailToSelected = () => {
    sessionStorage.setItem('cndb_email_recipients', JSON.stringify(selected));
    navigate('/emails/compose?to=selected');
  };

  const sendEmailToOne = (c) => {
    sessionStorage.setItem('cndb_email_recipients', JSON.stringify([c.id]));
    navigate('/emails/compose?to=selected');
  };

  const toggle = id => setSelected(s => s.includes(id) ? s.filter(i=>i!==id) : [...s,id]);
  const toggleAll = () => setSelected(s => s.length===contacts.length ? [] : contacts.map(c=>c.id));

  const StatusBadge = ({ s }) => {
    const map = { active:['badge-green','Ενεργός'], inactive:['badge-amber','Ανενεργός'], unsubscribed:['badge-red','Unsub'] };
    const [cls, lbl] = map[s] || ['badge-gray', s];
    return <span className={`badge ${cls}`}>{lbl}</span>;
  };

  const Th = ({ children, col, style }) => (
    <th onClick={col ? () => handleSort(col) : undefined} style={{ cursor: col?'pointer':'default', whiteSpace:'nowrap', ...style }}>
      {children}{col && sortBy===col ? (sortDir==='ASC' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const activeFilters = Object.values(filters).filter(Boolean).length;

  const Field = ({ label, value, copyable = true }) => (
    <div style={{ padding:'10px 0', borderBottom:'1px solid var(--fog)' }}>
      <div style={{ fontSize:'11px', color:'var(--mist)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px' }}>{label}</div>
      <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'14px' }}>
        <span style={{ color: value ? 'var(--ink)' : 'var(--cloud)', wordBreak:'break-word' }}>{value || '—'}</span>
        {copyable && value && <CopyBtn value={value} label={label} />}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', gap:'12px', flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontSize:'22px', fontWeight:700, letterSpacing:'-0.03em' }}>Επαφές</h1>
          <p style={{ color:'var(--slate)', fontSize:'12px', marginTop:'3px' }}>{total.toLocaleString('el-GR')} επαφές συνολικά</p>
        </div>
        {canEdit && (
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            <label className="btn btn-ghost btn-sm" style={{ cursor:'pointer' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Import
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} style={{ display:'none' }} />
            </label>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('excel')}>Excel</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('csv')}>CSV</button>
            <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Νέα Επαφή</button>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <div className="card" style={{ padding:'12px 14px', marginBottom:'12px' }}>
        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ position:'relative', flex:1, minWidth:'220px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--mist)' }}>
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.8"/><path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <input className="input input-sm" style={{ paddingLeft:'32px' }}
              placeholder="Αναζήτηση ονόματος, email, εταιρείας, τηλεφώνου..."
              onChange={e => { clearTimeout(window._st); window._st = setTimeout(() => { setSearch(e.target.value); setPage(1); }, 350); }} />
          </div>
          <button className={`btn btn-sm ${showFilters || activeFilters ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setShowFilters(!showFilters)}>
            Φίλτρα {activeFilters > 0 && `(${activeFilters})`}
          </button>
          {activeFilters > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilters({ status:'', city:'', industry:'', source:'', tag:'' }); setPage(1); }}>✕ Καθαρισμός</button>
          )}
          <select className="input input-sm" style={{ width:'auto' }} value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}>
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n} / σελίδα</option>)}
          </select>
        </div>

        {showFilters && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:'8px', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--silver)' }}>
            {[
              ['status','Κατάσταση',['active','inactive','unsubscribed']],
              ['city','Πόλη', opts.cities],
              ['industry','Κλάδος', opts.industries],
              ['source','Πηγή', opts.sources],
              ['tag','Ετικέτα', opts.tags],
            ].map(([k,lbl,options]) => (
              <select key={k} className="input input-sm" value={filters[k]}
                onChange={e => { setFilters(f => ({...f,[k]:e.target.value})); setPage(1); }}>
                <option value="">— {lbl} —</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
          </div>
        )}
      </div>

      {/* Bulk bar */}
      {selected.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:'10px', marginBottom:'12px', flexWrap:'wrap' }}>
          <span style={{ fontSize:'13px', fontWeight:600, color:'#1D4ED8' }}>{selected.length} επιλεγμένες</span>
          <div style={{ width:'1px', height:'16px', background:'#BFDBFE' }} />
          <button className="btn btn-blue btn-sm" onClick={sendEmailToSelected}>✉ Αποστολή Email</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setBulkVal(''); setModal('bulk-status'); }}>Αλλαγή κατάστασης</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setBulkVal(''); setModal('bulk-tag'); }}>+ Ετικέτα</button>
          <button className="btn btn-ghost btn-sm" onClick={() => handleExport('csv')}>Export</button>
          {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => { if(window.confirm(`Διαγραφή ${selected.length} επαφών;`)) handleBulk('delete'); }}>Διαγραφή</button>}
        </div>
      )}

      {/* Table */}
      <div className="tbl-wrap" style={{ overflowX:'auto' }}>
        <table style={{ minWidth:'1100px' }}>
          <thead>
            <tr>
              {canEdit && <th style={{ width:'34px', paddingRight:'4px' }}>
                <input type="checkbox" onChange={toggleAll} checked={selected.length===contacts.length && contacts.length>0} style={{ cursor:'pointer' }} />
              </th>}
              <Th col="first_name">Πλήρες Όνομα</Th>
              <Th col="first_name">Όνομα</Th>
              <Th col="last_name">Επίθετο</Th>
              <Th col="email">Email</Th>
              <th>Τηλέφωνο</th>
              <Th col="company">Εταιρεία</Th>
              <th>Κλάδος</th>
              <Th col="city">Πόλη</Th>
              <th>Ετικέτες</th>
              <th>Κατάσταση</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ textAlign:'center', padding:'48px' }}><span className="spin" /></td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign:'center', padding:'48px', color:'var(--mist)' }}>
                {search || activeFilters ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν επαφές ακόμα'}
              </td></tr>
            ) : contacts.map(c => (
              <tr key={c.id} className={selected.includes(c.id) ? 'selected' : ''} onClick={() => setViewContact(c)} style={{ cursor:'pointer' }}>
                {canEdit && <td onClick={e => e.stopPropagation()} style={{ paddingRight:'4px' }}>
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} style={{ cursor:'pointer' }} />
                </td>}
                <td style={{ fontWeight:600, fontSize:'13px', whiteSpace:'nowrap' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || <span style={{ color:'var(--cloud)', fontWeight:400 }}>—</span>}</td>
                <td style={{ fontSize:'13px' }}>{c.first_name || <span style={{ color:'var(--cloud)' }}>—</span>}</td>
                <td style={{ fontSize:'13px' }}>{c.last_name || <span style={{ color:'var(--cloud)' }}>—</span>}</td>
                <td style={{ fontSize:'12px', color:'var(--slate)' }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:'2px' }}>
                    {c.email}
                    <span onClick={e => e.stopPropagation()}><CopyBtn value={c.email} label="Email" /></span>
                  </span>
                </td>
                <td style={{ fontSize:'12px', whiteSpace:'nowrap' }}>{c.phone || <span style={{ color:'var(--cloud)' }}>—</span>}</td>
                <td style={{ fontSize:'12px', maxWidth:'160px' }} className="truncate">{c.company || <span style={{ color:'var(--cloud)' }}>—</span>}</td>
                <td style={{ fontSize:'12px', color:'var(--slate)', maxWidth:'120px' }} className="truncate">{c.industry || '—'}</td>
                <td style={{ fontSize:'12px' }}>{c.city || <span style={{ color:'var(--cloud)' }}>—</span>}</td>
                <td style={{ maxWidth:'180px' }}><TagChips tags={c.tags} /></td>
                <td><StatusBadge s={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'14px', flexWrap:'wrap', gap:'10px' }}>
        <span style={{ fontSize:'12px', color:'var(--slate)' }}>
          Σελίδα <strong>{page}</strong> από <strong>{pages}</strong> · {total.toLocaleString('el-GR')} επαφές · {limit}/σελίδα
        </span>
        <div className="pager">
          <button className="pager-btn" onClick={() => setPage(1)} disabled={page===1}>«</button>
          <button className="pager-btn" onClick={() => setPage(p=>p-1)} disabled={page===1}>‹</button>
          {Array.from({ length: Math.min(5, pages) }, (_, i) => {
            const p = Math.max(1, Math.min(page-2, pages-4)) + i;
            if (p > pages) return null;
            return <button key={p} className={`pager-btn ${p===page?'active':''}`} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button className="pager-btn" onClick={() => setPage(p=>p+1)} disabled={page===pages}>›</button>
          <button className="pager-btn" onClick={() => setPage(pages)} disabled={page===pages}>»</button>
        </div>
      </div>

      {/* ─── CONTACT DETAIL CARD ─── */}
      {viewContact && (
        <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && setViewContact(null)}>
          <div className="modal-box" style={{ maxWidth:'520px' }}>
            {/* Card header */}
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                <div style={{
                  width:'52px', height:'52px', borderRadius:'50%', flexShrink:0,
                  background:'linear-gradient(135deg,#0A0F1E,#1B4FD8)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'19px', fontWeight:700, color:'#fff', fontFamily:'Inter,sans-serif'
                }}>
                  {(viewContact.first_name?.[0] || viewContact.email[0]).toUpperCase()}
                </div>
                <div>
                  <h3 style={{ fontSize:'17px', fontWeight:700, letterSpacing:'-0.02em', lineHeight:1.2 }}>
                    {[viewContact.first_name, viewContact.last_name].filter(Boolean).join(' ') || viewContact.email}
                  </h3>
                  <div style={{ display:'flex', gap:'6px', marginTop:'5px', alignItems:'center', flexWrap:'wrap' }}>
                    {(() => { const map={active:['badge-green','Ενεργός'],inactive:['badge-amber','Ανενεργός'],unsubscribed:['badge-red','Unsubscribed']}; const [cls,lbl]=map[viewContact.status]||['badge-gray',viewContact.status]; return <span className={`badge ${cls}`}>{lbl}</span>; })()}
                    {viewContact.company && <span style={{ fontSize:'12px', color:'var(--slate)' }}>{viewContact.company}</span>}
                  </div>
                </div>
              </div>
              <button onClick={() => setViewContact(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--mist)', padding:'4px', fontSize:'18px', lineHeight:1 }}>✕</button>
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
              {canEdit && viewContact.status === 'active' && (
                <button className="btn btn-blue btn-sm" onClick={() => sendEmailToOne(viewContact)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Αποστολή Email
                </button>
              )}
              <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(viewContact.email); toast.success('Email αντιγράφηκε'); }}>
                Copy Email
              </button>
              {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(viewContact)}>✏ Επεξεργασία</button>}
              {isAdmin && <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)' }} onClick={() => handleDelete(viewContact.id)}>Διαγραφή</button>}
            </div>

            {/* All fields with copy */}
            <div style={{ maxHeight:'46vh', overflowY:'auto' }}>
              <Field label="Email" value={viewContact.email} />
              <Field label="Τηλέφωνο" value={viewContact.phone} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
                <Field label="Όνομα" value={viewContact.first_name} />
                <Field label="Επίθετο" value={viewContact.last_name} />
              </div>
              <Field label="Εταιρεία" value={viewContact.company} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
                <Field label="Κλάδος" value={viewContact.industry} />
                <Field label="Πόλη" value={viewContact.city} />
              </div>
              <div style={{ padding:'10px 0', borderBottom:'1px solid var(--fog)' }}>
                <div style={{ fontSize:'11px', color:'var(--mist)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Ετικέτες</div>
                <TagChips tags={viewContact.tags} max={99} />
              </div>
              <Field label="Πηγή" value={viewContact.source} copyable={false} />
              {viewContact.notes && <Field label="Σημειώσεις" value={viewContact.notes} copyable={false} />}
              <div style={{ padding:'10px 0', fontSize:'11px', color:'var(--mist)' }}>
                Προστέθηκε: {new Date(viewContact.created_at).toLocaleDateString('el-GR', { day:'numeric', month:'long', year:'numeric' })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal === 'form' && (
        <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && setModal(null)}>
          <div className="modal-box modal-box-lg">
            <h3 className="modal-title">{editId ? 'Επεξεργασία Επαφής' : 'Νέα Επαφή'}</h3>
            <div className="form-row">
              {[['first_name','Όνομα'],['last_name','Επίθετο']].map(([k,l]) => (
                <div key={k} className="form-group">
                  <label className="form-label">{l}</label>
                  <input className="input" value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="input" type="email" value={form.email||''} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required />
            </div>
            <div className="form-row-3">
              {[['phone','Τηλέφωνο'],['company','Εταιρεία'],['industry','Κλάδος'],['city','Πόλη'],['country','Χώρα'],['source','Πηγή']].map(([k,l]) => (
                <div key={k} className="form-group">
                  <label className="form-label">{l}</label>
                  <input className="input" value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
                </div>
              ))}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Ετικέτες (με κόμμα)</label>
                <input className="input" value={form.tags||''} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="ΠΕΛΑΤΗΣ,Προσκλήσεις" />
              </div>
              <div className="form-group">
                <label className="form-label">Κατάσταση</label>
                <select className="input" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                  <option value="active">Ενεργός</option>
                  <option value="inactive">Ανενεργός</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Σημειώσεις</label>
              <textarea className="input" rows={3} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Ακύρωση</button>
              <button className="btn btn-primary" onClick={handleSave}>Αποθήκευση</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status */}
      {modal === 'bulk-status' && (
        <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && setModal(null)}>
          <div className="modal-box" style={{ maxWidth:'360px' }}>
            <h3 className="modal-title">Αλλαγή Κατάστασης</h3>
            <p style={{ color:'var(--slate)', fontSize:'13px', marginBottom:'14px' }}>{selected.length} επαφές επιλεγμένες</p>
            <select className="input" value={bulkVal} onChange={e => setBulkVal(e.target.value)}>
              <option value="">Επιλέξτε...</option>
              <option value="active">Ενεργός</option>
              <option value="inactive">Ανενεργός</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Ακύρωση</button>
              <button className="btn btn-primary" disabled={!bulkVal} onClick={() => handleBulk('status', bulkVal)}>Εφαρμογή</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Tag */}
      {modal === 'bulk-tag' && (
        <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && setModal(null)}>
          <div className="modal-box" style={{ maxWidth:'360px' }}>
            <h3 className="modal-title">Προσθήκη Ετικέτας</h3>
            <p style={{ color:'var(--slate)', fontSize:'13px', marginBottom:'14px' }}>{selected.length} επαφές επιλεγμένες</p>
            <input className="input" placeholder="π.χ. Προσκλήσεις" value={bulkVal} onChange={e => setBulkVal(e.target.value)} list="tag-suggestions" />
            <datalist id="tag-suggestions">
              {opts.tags.map(t => <option key={t} value={t} />)}
            </datalist>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Ακύρωση</button>
              <button className="btn btn-primary" disabled={!bulkVal} onClick={() => handleBulk('tag', bulkVal)}>Εφαρμογή</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
